/**
 * User Login Page
 *
 * Implements authentication flows using Supabase Auth:
 * - Email/password login
 * - Magic link (passwordless) sign-in
 *
 * Features:
 * - Form validation with react-hook-form and Zod
 * - Real-time error feedback
 * - Tab-based UI for password vs magic link login
 * - Email confirmation after signup
 * - Auto-redirect on successful authentication
 *
 * See: docs/v2/03-auth-and-security.md
 *
 * ## E2E test additions (GitHub issue #494 — "E2E: Authentication —
 * login, signup, OTP, password reset, session management")
 *
 * A few small, additive changes were made here specifically so this
 * page's behavior is deterministic and assertable from
 * `e2e/login-auth-flow.spec.ts`, without changing the underlying
 * Supabase-Auth-driven login flow:
 *
 * - A `sonner` `toast.error(...)` now fires alongside the existing inline
 *   `apiError` banner (`<Toaster />` is already mounted app-wide by
 *   `app/AppShell.tsx`, and every other error surface in this codebase —
 *   e.g. `@/lib/api/apiClient.ts`'s `reportApiError` — already uses
 *   `sonner` for this). `toFriendlyLoginError()` below normalizes
 *   Supabase's raw GoTrue error message to the ticket's expected
 *   "Invalid email or password" copy.
 *
 *   NOTE on the ticket's two separate bullets ("Invalid password ->
 *   'Invalid email or password' toast" vs. "Non-existent email -> 'No
 *   account found' toast"): Supabase Auth's `signInWithPassword`
 *   deliberately returns the *same* generic `"Invalid login credentials"`
 *   error for both a wrong password and an unknown email — this is
 *   intentional anti-enumeration behavior (RFC-recommended: don't let an
 *   attacker distinguish "wrong password" from "no such account").
 *   Fabricating a distinct "No account found" message would require an
 *   extra server-side "does this email exist?" lookup that itself
 *   reintroduces the user-enumeration hole the generic message exists to
 *   prevent, and no such check-a-user-exists endpoint exists in this
 *   codebase. So both cases intentionally surface the same
 *   "Invalid email or password" toast here; see the spec file's
 *   doc-comment for how it's tested.
 * - A password-visibility toggle (eye icon, `lucide-react`, already a
 *   project dependency) was added to the password field.
 * - The "Hero Hire AI" heading is now a `<Link to="/">`, matching the
 *   `<Link to="/">{brand}</Link>` "logo" pattern already used by
 *   `@/components/marketing/MarketingLayout.tsx`'s header (this page
 *   doesn't render inside that layout, so it didn't have one before).
 * - The submit button's loading label changed from "Signing in..." to
 *   "Logging in..." to match the ticket's explicit AC/demo-script text.
 */

import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { loginWithEmail, sendMagicLink } from '@/lib/auth/supabaseAuth';

/**
 * Normalizes a raw Supabase Auth error message to the user-facing copy
 * this ticket's test cases expect. See the module doc-comment above for
 * why "wrong password" and "non-existent email" intentionally map to the
 * same generic message.
 */
export function toFriendlyLoginError(message: string | undefined | null): string {
  if (!message) return 'Invalid email or password';
  const normalized = message.toLowerCase();
  if (normalized.includes('invalid login credentials') || normalized.includes('invalid email or password')) {
    return 'Invalid email or password';
  }
  return message;
}

/**
 * Validation schema for email/password login
 */
const LoginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

/**
 * Validation schema for magic link signup
 */
const MagicLinkSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
});

type LoginFormData = z.infer<typeof LoginSchema>;
type MagicLinkFormData = z.infer<typeof MagicLinkSchema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'password' | 'magic'>('password');
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [signupMessage] = useState<string | null>(
    (location.state as { message?: string })?.message || null
  );

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(LoginSchema),
  });

  const {
    register: registerMagic,
    handleSubmit: handleMagicSubmit,
    formState: { errors: magicErrors },
  } = useForm<MagicLinkFormData>({
    resolver: zodResolver(MagicLinkSchema),
  });

  const onPasswordSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true);
    setApiError(null);

    const { error } = await loginWithEmail(data.email, data.password);

    if (error) {
      const friendlyMessage = toFriendlyLoginError(error.message);
      setApiError(friendlyMessage);
      toast.error(friendlyMessage);
      setIsSubmitting(false);
      return;
    }

    // Redirect to dashboard on success. NOTE: the ticket's AC/QA text says
    // "redirects to /jobs" — there is no `/jobs` route anywhere in
    // `app/router.tsx` (the closest authenticated landing route is
    // `/dashboard`, which this has always redirected to). Redirecting to a
    // route that doesn't exist would just re-render the catch-all
    // `Navigate to="/"` route, so the existing `/dashboard` target is kept
    // and asserted on directly in `e2e/login-auth-flow.spec.ts`.
    navigate('/dashboard');
  };

  const onMagicSubmit = async (data: MagicLinkFormData) => {
    setIsSubmitting(true);
    setApiError(null);

    const { success, error } = await sendMagicLink(data.email);

    if (error || !success) {
      setApiError(error?.message || 'Failed to send magic link');
      setIsSubmitting(false);
      return;
    }

    // Show confirmation message
    setMagicLinkSent(true);
  };

  if (magicLinkSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-md p-8 space-y-6 bg-card border border-border rounded-lg shadow-lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-green-600">
              Check Your Email
            </h1>
            <p className="text-muted-foreground mt-4">
              We've sent a magic link to your email. Click the link to sign in.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              The link expires in 24 hours.
            </p>
            <button
              onClick={() => {
                setMagicLinkSent(false);
                setActiveTab('magic');
              }}
              className="mt-6 text-primary hover:underline font-medium"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md p-8 space-y-6 bg-card border border-border rounded-lg shadow-lg">
        <div className="text-center">
          <Link to="/" data-testid="login-logo">
            <h1 className="text-3xl font-bold">Hero Hire AI</h1>
          </Link>
          <p className="text-muted-foreground mt-2">Sign in to your account</p>
        </div>

        {signupMessage && (
          <div className="p-4 bg-blue-50 border border-blue-200 text-blue-800 rounded-md text-sm">
            {signupMessage}
          </div>
        )}

        {apiError && (
          <div className="p-4 bg-destructive/10 border border-destructive/30 text-destructive rounded-md text-sm">
            {apiError}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-border">
          <button
            onClick={() => setActiveTab('password')}
            className={`flex-1 py-2 text-center font-medium transition-colors ${
              activeTab === 'password'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Password
          </button>
          <button
            onClick={() => setActiveTab('magic')}
            className={`flex-1 py-2 text-center font-medium transition-colors ${
              activeTab === 'magic'
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Magic Link
          </button>
        </div>

        {/* Password Login Form */}
        {activeTab === 'password' && (
          <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                placeholder="you@example.com"
                {...registerPassword('email')}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {passwordErrors.email && (
                <p className="mt-1 text-sm text-destructive">
                  {passwordErrors.email.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  {...registerPassword('password')}
                  className="w-full px-3 py-2 pr-10 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="toggle-password-visibility"
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {passwordErrors.password && (
                <p className="mt-1 text-sm text-destructive">
                  {passwordErrors.password.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-4 py-2 bg-primary text-primary-foreground font-medium rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Logging in...' : 'Sign In'}
            </button>

            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-primary hover:underline font-medium"
              >
                Forgot password?
              </Link>
            </div>
          </form>
        )}

        {/* Magic Link Form */}
        {activeTab === 'magic' && (
          <form onSubmit={handleMagicSubmit(onMagicSubmit)} className="space-y-4">
            <div>
              <label htmlFor="magicEmail" className="block text-sm font-medium mb-2">
                Email
              </label>
              <input
                id="magicEmail"
                type="email"
                placeholder="you@example.com"
                {...registerMagic('email')}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {magicErrors.email && (
                <p className="mt-1 text-sm text-destructive">
                  {magicErrors.email.message}
                </p>
              )}
            </div>

            <p className="text-sm text-muted-foreground">
              We'll send you a magic link to sign in without a password.
            </p>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-4 py-2 bg-primary text-primary-foreground font-medium rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Sending link...' : 'Send Magic Link'}
            </button>
          </form>
        )}

        {/* Sign Up Link */}
        <div className="text-center text-sm text-muted-foreground">
          Don't have an account?{' '}
          <Link to="/signup" className="text-primary hover:underline font-medium">
            Create one
          </Link>
        </div>
      </div>
    </div>
  );
}
